import { useQuery } from '@tanstack/react-query'
import { getAnalyticsBuyers, type AnalyticsPeriodParams } from '../../shared/api/analytics.api'
import { Spinner } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useAnalyticsTable, type ColumnDef } from './shared/useAnalyticsTable'
import { SortableTh } from './shared/SortableTh'

/**
 * Вкладка «Закупщики» модуля «Аналитика» (этап 5 docs/analytics-plan.md).
 * Сводная таблица: квоты, запросы поставщикам, win-rate, широта базы.
 * Сортировка по всем колонкам + фильтр по закупщику.
 */

type BuyerRow = {
  buyerId: string
  name: string
  quotesCreated: number
  supplierRequests: number
  wonQuotes: number
  winRate: number
  uniqueSuppliers: number
}

const COLUMNS: ColumnDef<BuyerRow>[] = [
  { key: 'name', sort: (r) => r.name.toLowerCase(), filter: (r) => r.name },
  { key: 'quotesCreated', sort: (r) => r.quotesCreated },
  { key: 'supplierRequests', sort: (r) => r.supplierRequests },
  { key: 'wonQuotes', sort: (r) => r.wonQuotes },
  { key: 'winRate', sort: (r) => r.winRate },
  { key: 'uniqueSuppliers', sort: (r) => r.uniqueSuppliers },
]

export function BuyersTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-buyers', periodParams],
    queryFn: () => getAnalyticsBuyers(periodParams),
  })

  const table = useAnalyticsTable((data?.rows ?? []) as BuyerRow[], COLUMNS, undefined, 'buyers')

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (isError || !data) {
    return <ErrorMessage error="Не удалось загрузить аналитику по закупщикам" />
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900">Закупщики — сводка за период</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Создано квот, отправлено запросов поставщикам, квот ушло в заказ (win-rate), широта базы
        поставщиков.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <SortableTh table={table} col="name" className="pr-3">
                Закупщик
              </SortableTh>
              <SortableTh table={table} col="quotesCreated" align="right" className="px-3">
                Квот создано
              </SortableTh>
              <SortableTh table={table} col="supplierRequests" align="right" className="px-3">
                Запросов поставщикам
              </SortableTh>
              <SortableTh table={table} col="wonQuotes" align="right" className="px-3">
                Квот в заказ
              </SortableTh>
              <SortableTh table={table} col="winRate" align="right" className="px-3">
                Win-rate
              </SortableTh>
              <SortableTh table={table} col="uniqueSuppliers" align="right" className="pl-3">
                Поставщиков
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.view.map((r) => (
              <tr key={r.buyerId} className="border-b border-gray-50">
                <td className="py-2 pr-3 font-medium text-gray-800">{r.name}</td>
                <td className="py-2 px-3 text-right text-gray-800">{r.quotesCreated}</td>
                <td className="py-2 px-3 text-right text-gray-600">{r.supplierRequests}</td>
                <td className="py-2 px-3 text-right text-gray-600">{r.wonQuotes}</td>
                <td className="py-2 px-3 text-right font-medium text-gray-700">{r.winRate}%</td>
                <td className="py-2 pl-3 text-right text-gray-600">{r.uniqueSuppliers}</td>
              </tr>
            ))}
            {table.view.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-sm text-gray-400">
                  Нет закупщиков
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-400">
        Win-rate считается по полю orderedQuoteId (этап 2): заполняется при оформлении новых
        заказов, по историческим данным может быть пустым.
      </p>
    </div>
  )
}
